import React, { useEffect, useRef, useState } from 'react';
import { Building2, TrendingDown, TrendingUp, Zap } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const Stats = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState([0, 0, 0, 0]);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;
    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCounts([
        Math.round(1.5 * easeOut * 10) / 10,
        Math.round(60 * easeOut),
        Math.round(50 * easeOut),
        Math.round(70 * easeOut)
      ]);
      if (currentStep >= steps) clearInterval(interval);
    }, stepDuration);
    return () => clearInterval(interval);
  }, [isVisible]);

  const stats = [
    { value: `${counts[0]}M`, label: t('stats.production'), icon: Building2 },
    { value: `-${counts[1]}%`, label: t('stats.costReduction'), icon: TrendingDown },
    { value: `+${counts[2]}%`, label: t('stats.profitability'), icon: TrendingUp },
    { value: `${counts[3]}%`, label: t('stats.faster'), icon: Zap }
  ];

  return (
    <section ref={sectionRef} className="py-24 bg-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, black 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
      </div>
      <div className="absolute top-20 left-20 w-20 h-20 bg-red-100 rounded-full blur-2xl animate-pulse" />
      <div className="absolute bottom-20 right-20 w-32 h-32 bg-red-50 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 bg-red-50 text-red-600 text-sm font-semibold uppercase tracking-wider px-4 py-2 rounded-full mb-4">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-ping" />
            {t('stats.badge')}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mt-4">{t('stats.title')}</h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index}
                className={`group relative bg-neutral-50 rounded-2xl p-8 border border-neutral-100 hover:border-red-200 transition-all duration-500 hover:shadow-xl hover:-translate-y-3 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}>
                <div className="w-14 h-14 rounded-xl bg-red-50 flex items-center justify-center mb-6 group-hover:bg-red-100 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6">
                  <Icon className="text-red-600 transition-transform duration-500 group-hover:scale-110" size={28} />
                </div>
                <div className="text-4xl font-bold text-neutral-900 mb-2 tabular-nums">
                  {isVisible ? stat.value : '0'}
                </div>
                <p className="text-neutral-600">{stat.label}</p>
                <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-red-600 rounded-b-2xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
              </div>
            );
          })}
        </div>
        <div className="text-center mt-16">
          <p className="text-neutral-500 text-sm">{t('stats.disclaimer')}</p>
        </div>
      </div>
    </section>
  );
};

export default Stats;
